import axios, { AxiosRequestConfig } from 'axios';
import config from '@/config';
import { auth0 } from '@/main';
import { jwtDecode } from 'jwt-decode';

import { isRequestToTheServer } from './helpers';

// ============================================= Interfaces =============================================
interface Get {
  (
    url: string,
    config?: {
      headers?: object;
      params?: object;
    },
    hasAuth?: boolean,
  ): Promise<{ success: boolean; data: any }>;
}

interface Del {
  (
    url: string,
    config?: {
      headers?: object;
      params?: object;
    },
    hasAuth?: boolean,
  ): Promise<{ success: boolean; data: any }>;
}

interface Post {
  (
    url: string,
    reqBody: object,
    config?: {
      headers?: object;
      params?: object;
    },
    hasAuth?: boolean,
  ): Promise<{ success: boolean; data: any }>;
}

interface Put {
  (
    url: string,
    reqBody: object,
    config?: {
      headers?: object;
      params?: object;
    },
    hasAuth?: boolean,
  ): Promise<{ success: boolean; data: any }>;
}
interface Patch {
  (
    url: string,
    reqBody: object,
    config?: {
      headers?: object;
      params?: object;
    },
    hasAuth?: boolean,
  ): Promise<{ success: boolean; data: any }>;
}
// ============================================= Interfaces =============================================

// ========================================== Helper functions ==========================================
async function getToken() {
  // Check if the token is already in the local storage
  const storedToken = localStorage.getItem('auth0_token');

  if (storedToken) {
    try {
      // Check if token is expired
      const decodedToken = jwtDecode(storedToken);
      if (decodedToken.exp < Date.now() / 1000) {
        localStorage.removeItem('auth0_token');
      }
      return storedToken;
    } catch (error) {
      // If token is invalid, remove it
      localStorage.removeItem('auth0_token');
    }
  }

  // If token is not in the local storage, get it from Auth0
  // This is a workaround to avoid the issue of "Login Required" (happening randomly)
  await new Promise((resolve) => setTimeout(resolve, 500));
  const token = await auth0.getAccessTokenSilently();
  localStorage.setItem('auth0_token', token);
  return token;
}

const axiosApi = axios.create({
  baseURL: config.baseUrls.api || 'http://localhost:5003/api/v1',
});

export const get: Get = async function (url, config = {}, hasAuth = true) {
  try {
    if (hasAuth) {
      const token = await getToken();
      config = {
        ...config,
        headers: {
          ...config.headers,
          authorization: `Bearer ${token}`,
        },
      };
    }
    const { data } = await axiosApi.get(url, {
      ...config,
    } as AxiosRequestConfig);

    if (isRequestToTheServer(data)) {
      return data;
    }
    return { success: true, data };
  } catch (error) {
    if (isRequestToTheServer(error)) {
      return error;
    }
    return { success: false, data: error };
  }
};
export const post: Post = async function (url, reqBody, config = {}, hasAuth = true) {
  try {
    if (hasAuth) {
      const token = await getToken();
      config = {
        ...config,
        headers: {
          ...config.headers,
          authorization: `Bearer ${token}`,
        },
      };
    }
    const { data } = await axiosApi.post(url, { ...reqBody }, {
      ...config,
    } as AxiosRequestConfig);
    if (isRequestToTheServer(data)) {
      return data;
    }
    return { success: true, data };
  } catch (error) {
    if (isRequestToTheServer(error)) {
      return error;
    }
    return { success: false, data: error };
  }
};

export const postFormData: Post = async function (url, formData, config = {}, hasAuth = true) {
  try {
    if (hasAuth) {
      const token = await getToken();
      config = {
        ...config,
        headers: {
          ...config.headers,
          authorization: `Bearer ${token}`,
        },
      };
    }
    const { data } = await axiosApi.post(url, formData, {
      ...config,
    } as AxiosRequestConfig);
    if (isRequestToTheServer(data)) {
      return data;
    }
    return { success: true, data };
  } catch (error) {
    if (isRequestToTheServer(error)) {
      return error;
    }
    return { success: false, data: error };
  }
};

export const put: Put = async function (url, reqBody, config = {}, hasAuth = true) {
  try {
    if (hasAuth) {
      const token = await getToken();
      config = {
        ...config,
        headers: {
          ...config.headers,
          authorization: `Bearer ${token}`,
        },
      };
    }
    const { data } = await axiosApi.put(url, { ...reqBody }, {
      ...config,
    } as AxiosRequestConfig);

    if (isRequestToTheServer(data)) {
      return data;
    }
    return { success: true, data };
  } catch (error) {
    if (isRequestToTheServer(error)) {
      return error;
    }
    return { success: false, data: error };
  }
};

export const del: Del = async function (url, config = {}, hasAuth = true) {
  try {
    if (hasAuth) {
      const token = await getToken();
      config = {
        ...config,
        headers: {
          ...config.headers,
          authorization: `Bearer ${token}`,
        },
      };
    }
    const { data } = await axiosApi.delete(url, {
      ...config,
    } as AxiosRequestConfig);

    if (isRequestToTheServer(data)) {
      return data;
    }
    return { success: true, data };
  } catch (error) {
    if (isRequestToTheServer(error)) {
      return error;
    }
    return { success: false, data: error };
  }
};

export const patch: Patch = async function (url, reqBody, config = {}, hasAuth = true) {
  try {
    if (hasAuth) {
      const token = await getToken();
      config = {
        ...config,
        headers: {
          ...config.headers,
          authorization: `Bearer ${token}`,
        },
      };
    }

    const { data } = await axiosApi.patch(url, { ...reqBody }, {
      ...config,
    } as AxiosRequestConfig);

    if (isRequestToTheServer(data)) {
      return data;
    }
    return { success: true, data };
  } catch (error) {
    if (isRequestToTheServer(error)) {
      return error;
    }
    return { success: false, data: error };
  }
};
